/**
 * Pathfinding (Movement spec §2.1). Weighted A* over the shared nav grid.
 *
 * A note on JPS: the spec asks for Jump Point Search, but canonical JPS is only valid
 * on a UNIFORM-cost grid — it proves it can skip nodes by assuming every step costs
 * the same. Our grid is deliberately weighted (roads 0.6, shroom 2.0, bridge 1.5,
 * ramp 1.3) so agents visibly prefer roads and weigh shrooms — that terrain weighting
 * is the thing that reads as "human". JPS would silently ignore those weights and
 * route through shroom clouds and off roads. So this is weighted A* with the octile
 * heuristic and a hard node budget instead; the budget + the O(1) region check keep
 * it fast enough for 25 bots. (If we ever drop per-terrain weights, JPS drops in here
 * behind the same signature.)
 *
 * - Octile heuristic, tie-broken to avoid fan-shaped expansion.
 * - No diagonal corner-cutting (shared diagonalOpen rule).
 * - Region short-circuit: unreachable goals return immediately, no flood.
 * - Hard budget of 2000 expanded nodes; on overflow return the best partial path
 *   toward the lowest-h node found — a path that moves beats a stall.
 */

import { NavGrid, tileIndex, diagonalOpen, sameRegion } from './grid';

const SQRT2 = Math.SQRT2;
const DIRS8 = [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1]] as const;
export const MAX_EXPAND = 2000;

// Octile heuristic (spec §2.1): D·(dx+dy) + (D2−2D)·min(dx,dy), D=1, D2=√2.
function octile(dx: number, dy: number): number {
  const ax = Math.abs(dx), ay = Math.abs(dy);
  return (ax + ay) + (SQRT2 - 2) * Math.min(ax, ay);
}

// Binary min-heap over f-score, storing tile indices.
class Heap {
  private f: number[] = [];
  private t: number[] = [];
  get size() { return this.f.length; }
  push(f: number, t: number) {
    this.f.push(f); this.t.push(t);
    let n = this.f.length - 1;
    while (n > 0) { const p = (n - 1) >> 1; if (this.f[p] <= this.f[n]) break; this.sw(p, n); n = p; }
  }
  pop(): number {
    const t = this.t[0], lf = this.f.pop()!, lt = this.t.pop()!;
    if (this.f.length) { this.f[0] = lf; this.t[0] = lt; this.down(0); }
    return t;
  }
  private down(n: number) {
    for (;;) {
      const l = 2 * n + 1, r = l + 1; let s = n;
      if (l < this.f.length && this.f[l] < this.f[s]) s = l;
      if (r < this.f.length && this.f[r] < this.f[s]) s = r;
      if (s === n) break; this.sw(s, n); n = s;
    }
  }
  private sw(a: number, b: number) {
    [this.f[a], this.f[b]] = [this.f[b], this.f[a]];
    [this.t[a], this.t[b]] = [this.t[b], this.t[a]];
  }
}

export interface PathResult {
  /** Tile-centre waypoints (x = tx+0.5). Empty if nothing usable. */
  path: { x: number; y: number }[];
  found: boolean;       // a complete path to the goal
  partial: boolean;     // budget/dead-end: best effort toward the goal
  unreachable: boolean; // different region — no path exists
  expanded: number;     // nodes popped (for instrumentation)
}

export interface PathOptions {
  /** Extra additive cost per tile (dynamic layer, danger map). */
  dynamicCost?: (tile: number) => number;
  maxExpand?: number;
}

/** Weighted A* from (sx,sy) to (gx,gy) over the grid. Coordinates are tiles. */
export function findPath(grid: NavGrid, sx: number, sy: number, gx: number, gy: number, opts: PathOptions = {}): PathResult {
  const { w, h, walkable, cost } = grid;
  const empty = (extra: Partial<PathResult>): PathResult => ({ path: [], found: false, partial: false, unreachable: false, expanded: 0, ...extra });
  if (sx < 0 || sy < 0 || sx >= w || sy >= h || gx < 0 || gy < 0 || gx >= w || gy >= h) return empty({});
  const start = tileIndex(sx, sy, w), goal = tileIndex(gx, gy, w);
  if (!walkable[start] || !walkable[goal]) return empty({});
  if (start === goal) return { path: [center(sx, sy)], found: true, partial: false, unreachable: false, expanded: 0 };
  // O(1) reachability: different region → no path, don't flood the map.
  if (!sameRegion(grid, sx, sy, gx, gy)) return empty({ unreachable: true });

  const dyn = opts.dynamicCost;
  const budget = opts.maxExpand ?? MAX_EXPAND;
  const n = w * h;
  const gScore = new Float64Array(n).fill(Infinity);
  const came = new Int32Array(n).fill(-1);
  const closed = new Uint8Array(n);
  gScore[start] = 0;
  const heap = new Heap();
  heap.push(octile(gx - sx, gy - sy), start);

  let expanded = 0;
  let bestNode = start, bestH = octile(gx - sx, gy - sy);   // for the partial fallback

  while (heap.size) {
    const cur = heap.pop();
    if (closed[cur]) continue;
    closed[cur] = 1;
    expanded++;
    if (cur === goal) return { path: reconstruct(came, cur, w), found: true, partial: false, unreachable: false, expanded };

    const cx = cur % w, cy = (cur - (cur % w)) / w;
    const hcur = octile(gx - cx, gy - cy);
    if (hcur < bestH) { bestH = hcur; bestNode = cur; }

    if (expanded >= budget) break;   // overflow → return best partial below

    for (const [dx, dy] of DIRS8) {
      const nx = cx + dx, ny = cy + dy;
      if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
      const ni = tileIndex(nx, ny, w);
      if (!walkable[ni] || closed[ni]) continue;
      if (!diagonalOpen(walkable, w, h, cx, cy, dx, dy)) continue;
      const step = (dx !== 0 && dy !== 0 ? SQRT2 : 1) * cost[ni] + (dyn ? dyn(ni) : 0);
      const ng = gScore[cur] + step;
      if (ng < gScore[ni]) {
        gScore[ni] = ng;
        came[ni] = cur;
        // Tie-break the heuristic slightly to kill fan-shaped expansion (spec §2.1).
        const f = ng + octile(gx - nx, gy - ny) * (1 + 1 / 1000);
        heap.push(f, ni);
      }
    }
  }

  // No complete path within budget/region: best effort toward the closest node.
  if (bestNode !== start) {
    return { path: reconstruct(came, bestNode, w), found: false, partial: true, unreachable: false, expanded };
  }
  return empty({ expanded });
}

const center = (tx: number, ty: number) => ({ x: tx + 0.5, y: ty + 0.5 });

function reconstruct(came: Int32Array, node: number, w: number): { x: number; y: number }[] {
  const out: { x: number; y: number }[] = [];
  let c = node;
  while (c !== -1) { const x = c % w, y = (c - (c % w)) / w; out.push(center(x, y)); c = came[c]; }
  return out.reverse();
}
